import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { Category } from "../advanced-flow/triggers";

export enum PhoneNumberUseCase {
  Marketing = "marketing",
  Transactional = "transactional",
  Support = "support",
  Recover = "recover",
}

// TODO: Support and Transactional use cases just default to Marketing for now
export const phoneNumberUseCaseToCategory: Record<
  PhoneNumberUseCase,
  Category
> = {
  [PhoneNumberUseCase.Marketing]: "Marketing",
  [PhoneNumberUseCase.Support]: "Marketing",
  [PhoneNumberUseCase.Transactional]: "Marketing",
  [PhoneNumberUseCase.Recover]: "Recover",
};

const CATEGORIES_THAT_USE_MARKETING_PHONE_NUMBER: Category[] = ["Marketing"];
const CATEGORIES_THAT_USE_RECOVER_PHONE_NUMBER: Category[] = [
  "Recover",
  "Return tracking",
];

export function getPhoneNumberUseCaseFromCategory(
  category: Category,
): PhoneNumberUseCase {
  if (CATEGORIES_THAT_USE_MARKETING_PHONE_NUMBER.includes(category)) {
    return PhoneNumberUseCase.Marketing;
  }
  if (CATEGORIES_THAT_USE_RECOVER_PHONE_NUMBER.includes(category)) {
    return PhoneNumberUseCase.Recover;
  }
  throw new Error(
    `Category ${category} does not map to any phone number use case`,
  );
}

export enum PhoneNumberProvider {
  Telnyx = "telnyx",
  Bandwidth = "bandwidth",
  RedoIMessage = "redo-imessage",
}

export enum TextMessagingVerificationStatus {
  Unverified = "unverified",
  Pending = "pending",
  Verified = "verified",
}

export enum PortEventType {
  PortIn = "port-in",
  PortOut = "port-out",
}

export enum ProviderTransitionType {
  FullPort = "full-port",
  HostedMessaging = "hosted-messaging",
}

interface VoiceMetadata {
  billingGroupId: string;
  sipConnectionId: string;
}

/**
 * Used to track the transition of a phone number from one provider to another.
 * After initiating a transition, the true transition will randomly occur
 * sometime within the next two weeks. During that time, we need to be prepared
 * to send from either provider until the transition is fully complete.
 */
const ProviderTransitionSchema_ = z.object({
  toProvider: z.enum(PhoneNumberProvider),
  /** The type of transition - full port transfers ownership, hosted messaging only transfers messaging */
  transitionType: z.enum(ProviderTransitionType).optional(),
  startedAt: z.date(),
  portCompletedAt: z.date().nullish(),
});
export const ProviderTransitionSchema: z.ZodType<ProviderTransition> =
  ProviderTransitionSchema_;
export interface ProviderTransition
  extends z.infer<typeof ProviderTransitionSchema_> {}

export const TRANSITION_PROBE_MESSAGE_TEXT =
  "__testing bandwidth messaging capability__";

export const TRANSITION_PROBE_TAG = "transition-probe";

/**
 * Schema for individual port history events
 */
const PortHistoryEventSchema_ = z.object({
  eventType: z.enum(PortEventType),
  provider: z.enum(PhoneNumberProvider),
  status: z.string(),
  timestamp: z.date(),
  rawWebhook: z.unknown().nullish(),
});
export const PortHistoryEventSchema: z.ZodType<PortHistoryEvent> =
  PortHistoryEventSchema_;
export interface PortHistoryEvent
  extends z.infer<typeof PortHistoryEventSchema_> {}

export const PhoneNumberSchema = z.object({
  _id: zExt.objectId(),
  number: z.string(),
  team: zExt.objectId(),
  provider: createEnum(PhoneNumberProvider),
  useCase: createEnum(PhoneNumberUseCase),
  alphanumericSenderId: z.string().nullish(),
  messagingMetadata: z
    .object({
      verificationStatus: createEnum(TextMessagingVerificationStatus).default(
        TextMessagingVerificationStatus.Unverified,
      ),
    })
    .optional(),
  voiceMetadata: z
    .object({ billingGroupId: z.string(), sipConnectionId: z.string() })
    .optional(),
  providerTransition: ProviderTransitionSchema.nullish(),
  portHistory: z.array(PortHistoryEventSchema).nullish(),
  telnyx: z
    .object({
      phoneNumberId: z.string().nullish(),
      messagingProfileId: z.string().nullish(),
      tollFreeVerificationId: z.string().nullish(),
    })
    .nullish(),
});

export type PhoneNumber = z.infer<typeof PhoneNumberSchema>;

export type MessagingMetadata = NonNullable<PhoneNumber["messagingMetadata"]>;

export type VoiceEnabledPhoneNumber = PhoneNumber & {
  voiceMetadata: VoiceMetadata;
};

export function isVoiceEnabledPhoneNumber(
  phoneNumber: PhoneNumber,
): phoneNumber is VoiceEnabledPhoneNumber {
  return !!phoneNumber.voiceMetadata;
}

export function determineCountryCode(phoneNumber: string): string | undefined {
  if (phoneNumber.startsWith("+1")) {
    return "US";
  }
  // extend for other country codes as needed
  return undefined;
}

const TollFreePhoneNumberProvisionRequest_ = z.object({
  teamId: z.string(),
  useCase: z.enum(PhoneNumberUseCase),
  businessName: z.string(),
  website: z.string(),
  businessAddress: z.object({
    street1: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  }),
  businessContact: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phone: z.string(),
  }),
  businessRegistrationNumber: z.string().optional(),
  businessRegistrationCountry: z.enum(["US", "CA"]).optional(),
  logoBase64: z.string().optional(),
});

export const TollFreePhoneNumberProvisionRequestSchema: z.ZodType<TollFreePhoneNumberProvisionRequest> =
  TollFreePhoneNumberProvisionRequest_;
export interface TollFreePhoneNumberProvisionRequest
  extends z.infer<typeof TollFreePhoneNumberProvisionRequest_> {}

const TollFreePhoneNumberProvisionResponse_ = z.object({
  useCase: z.enum(PhoneNumberUseCase),
  status: z.enum(["success", "failed"]),
  phoneNumber: z.string().optional(),
  message: z.string(),
});

export const TollFreePhoneNumberProvisionResponseSchema: z.ZodType<TollFreePhoneNumberProvisionResponse> =
  TollFreePhoneNumberProvisionResponse_;
export interface TollFreePhoneNumberProvisionResponse
  extends z.infer<typeof TollFreePhoneNumberProvisionResponse_> {}

const TollFreeVerificationStatusResponse_ = z.object({
  phoneNumber: z.string(),
  status: z.enum([
    "pending",
    "verified",
    "unverified",
    "rejected",
    "unknown", // API error or unable to fetch status
  ]),
  message: z.string(),
});

export const TollFreeVerificationStatusResponseSchema: z.ZodType<TollFreeVerificationStatusResponse> =
  TollFreeVerificationStatusResponse_;
export interface TollFreeVerificationStatusResponse
  extends z.infer<typeof TollFreeVerificationStatusResponse_> {}
